import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MapIcon, Check } from 'lucide-react';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { MAP_STYLES } from './constants';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MapStyle } from './types';

interface MapStyleControlProps {
	currentStyle: MapStyle;
	onStyleChange: (style: MapStyle) => void;
}

export function MapStyleControl({ currentStyle, onStyleChange }: MapStyleControlProps) {
	const [isOpen, setIsOpen] = useState(false);
	const { isDesktop } = useIsDesktopSize();

	const handleStyleSelect = (style: MapStyle) => {
		onStyleChange(style);
	};

	const currentStyleData = MAP_STYLES.find(style => style.id === currentStyle);
	const currentStyleName = currentStyleData?.name || 'Light';
	const currentStyleIcon = currentStyleData?.icon || <MapIcon className="h-3 w-3" />;

	if (isDesktop) {
		return (
			<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						className="h-8 px-3 bg-white/50 dark:bg-slate-800/50 hover:bg-white/80 dark:hover:bg-slate-800/80"
					>
						{currentStyleIcon}
						<span className="ml-1">{currentStyleName}</span>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-36 p-2">
					{MAP_STYLES.map(style => (
						<DropdownMenuItem
							key={style.id}
							onClick={() => handleStyleSelect(style.id)}
							className="flex items-center justify-between cursor-pointer px-3 py-2"
						>
							<div className="flex items-center space-x-2">
								{style.icon}
								<span>{style.name}</span>
							</div>
							{currentStyle === style.id && <Check className="h-3 w-3" />}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		);
	}

	return (
		<Accordion type="single" collapsible defaultValue="map-style" className="w-full">
			<AccordionItem value="map-style">
				<AccordionTrigger className="text-sm font-medium">Map Style</AccordionTrigger>
				<AccordionContent>
					<div className="space-y-2 pt-2">
						{MAP_STYLES.map(style => (
							<Button
								key={style.id}
								variant={currentStyle === style.id ? 'default' : 'ghost'}
								size="sm"
								onClick={() => handleStyleSelect(style.id)}
								className="w-full justify-start px-3 py-2"
							>
								{style.icon}
								<span className="ml-2">{style.name}</span>
								{currentStyle === style.id && <Check className="h-3 w-3 ml-auto" />}
							</Button>
						))}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}
